import React, { useState } from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Collapse,
    IconButton,
    Tooltip
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder'
import BookmarkIcon from '@material-ui/icons/Bookmark'

// LEIE dates arrive from OIG as YYYYMMDD strings (no separators). Parse manually
// rather than trusting Date.parse — a JS Date built from "20180131" would drift by
// timezone.
const formatLeieDate = (raw) => {
    if (!raw) return null
    const s = String(raw).trim()
    if (s.length !== 8 || !/^\d{8}$/.test(s)) return s
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
}

const readIdentity = (record) => {
    const isEntity = !!record?.is_entity
    const displayName = isEntity
        ? (record.bus_name || '(unnamed entity)')
        : [record.first_name, record.mid_name, record.last_name]
            .filter(Boolean).join(' ').trim() || '(unnamed)'

    return {
        displayName,
        recordHash: record?.record_hash || null,
        isEntity,
        isActive: record?.is_active !== false,
        reinDate: record?.rein_date || null,
        state: record?.state || null,
        city: record?.city || null,
        npi: record?.npi || null,
        exclType: record?.excl_type || null,
        exclDate: record?.excl_date || null,
        general: record?.general || null,
        specialty: record?.specialty || null,
    }
}

// Reinstated badge — LEIE never removes rows; a lifted exclusion flips is_active to false
// and stamps a rein_date. Underwriters need to see both the current status AND the historical
// exclusion, so we keep the row visible with a prominent badge.
const reinstatedBadge = (meta) => {
    if (meta.isActive) return null
    const formatted = formatLeieDate(meta.reinDate)
    const style = {
        marginLeft: 6, padding: '1px 6px', fontSize: 10,
        borderRadius: 3, fontWeight: 600, verticalAlign: 'middle',
        background: 'var(--badge-approved-bg)', color: 'var(--status-approved)'
    }
    return (
        <span style={style} title='Exclusion was later lifted by HHS OIG'>
            REINSTATED{formatted ? ` ${formatted}` : ''}
        </span>
    )
}

const ExpandableRow = ({ record, index, onSave, isSaved, saveEnabled }) => {
    const [open, setOpen] = useState(false)
    const meta = readIdentity(record)

    const savedTooltip = isSaved ? 'Already saved to this application' : 'Save this match to the application'
    const disabledTooltip = 'Only available inside an application'

    return (
        <>
            <TableRow hover style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
                <TableCell>
                    {meta.displayName}
                    {reinstatedBadge(meta)}
                </TableCell>
                <TableCell>{meta.isEntity ? 'Entity' : 'Individual'}</TableCell>
                <TableCell>{meta.npi || '—'}</TableCell>
                <TableCell>{meta.exclType || '—'}</TableCell>
                <TableCell>{formatLeieDate(meta.exclDate) || '—'}</TableCell>
                <TableCell>{meta.general || '—'}</TableCell>
                <TableCell>{meta.state || '—'}</TableCell>
                <TableCell align='right' padding='none' style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>
                    {onSave && (
                        <Tooltip title={saveEnabled ? savedTooltip : disabledTooltip} arrow>
                            <span>
                                <IconButton
                                    size='small'
                                    disabled={!saveEnabled || isSaved}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (!isSaved) onSave({ meta, record })
                                    }}
                                    aria-label={isSaved ? 'saved' : 'save'}
                                    id={`leie-save-${index}`}
                                >
                                    {isSaved ? <BookmarkIcon fontSize='small' color='primary'/> : <BookmarkBorderIcon fontSize='small'/>}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <IconButton
                        size='small'
                        aria-label={open ? 'collapse' : 'expand'}
                        id={`leie-toggle-${index}`}
                        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
                    >
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        <div style={{ padding: '12px 16px', fontSize: 13, lineHeight: 1.6 }}>
                            <div>
                                <strong>Status:</strong> {meta.isActive ? 'Excluded' : `Reinstated${formatLeieDate(meta.reinDate) ? ` on ${formatLeieDate(meta.reinDate)}` : ''}`}
                                &nbsp;&nbsp; <strong>Type:</strong> {meta.isEntity ? 'Entity' : 'Individual'}
                            </div>
                            <div>
                                <strong>Exclusion Type:</strong> {meta.exclType || '—'}
                                &nbsp;&nbsp; <strong>Exclusion Date:</strong> {formatLeieDate(meta.exclDate) || '—'}
                            </div>
                            {(record.general || record.specialty) && (
                                <div>
                                    <strong>Profession:</strong> {record.general || '—'}
                                    {record.specialty ? ` · ${record.specialty}` : ''}
                                </div>
                            )}
                            {(meta.npi || record.upin) && (
                                <div>
                                    {meta.npi && <><strong>NPI:</strong> {meta.npi} &nbsp;&nbsp; </>}
                                    {record.upin && <><strong>UPIN:</strong> {record.upin}</>}
                                </div>
                            )}
                            {!meta.isEntity && record.dob && (
                                <div><strong>DOB:</strong> {formatLeieDate(record.dob)}</div>
                            )}
                            {(record.address || record.city || record.state || record.zip) && (
                                <div>
                                    <strong>Address:</strong>{' '}
                                    {[record.address, record.city, record.state, record.zip]
                                        .filter(Boolean).join(', ')}
                                </div>
                            )}
                            {(record.waiver_date || record.wvr_state) && (
                                <div>
                                    <strong>Waiver:</strong>{' '}
                                    {record.waiver_date ? formatLeieDate(record.waiver_date) : '—'}
                                    {record.wvr_state ? ` (${record.wvr_state})` : ''}
                                </div>
                            )}
                        </div>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

const LeieResultsTable = ({ records, onSave, savedKeys = new Set(), saveEnabled = false }) => {
    return (
        <TableContainer component={Paper} className='mt-3'>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>NPI</TableCell>
                        <TableCell>Exclusion Type</TableCell>
                        <TableCell>Exclusion Date</TableCell>
                        <TableCell>Profession</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records.map((record, i) => {
                        const meta = readIdentity(record)
                        return (
                            <ExpandableRow
                                key={meta.recordHash || i}
                                record={record}
                                index={i}
                                onSave={onSave}
                                isSaved={savedKeys.has(meta.recordHash)}
                                saveEnabled={saveEnabled}
                            />
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default LeieResultsTable
